import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import DashboardService from '../../service';
import { useTranslation } from '../../i18n/TranslationContext';
import { useAuth } from '../../context/AuthContext';
import './vehicleImportCalculator.css';

const AUCTION_KEYS = ['iaai', 'copart', 'manheim'];

/** Normalize GET /calculator/transportprice JSON — field names vary by backend */
function parseTransportAmount(data) {
  if (data == null) return null;
  if (typeof data === 'number' && !Number.isNaN(data)) return data;
  if (typeof data === 'string') {
    const n = parseFloat(data);
    return Number.isFinite(n) ? n : null;
  }
  if (typeof data !== 'object') return null;
  const keys = [
    'transport',
    'transportPrice',
    'transport_price',
    'price',
    'amount',
    'total',
    'cost',
    'fee',
    'value',
  ];
  for (const k of keys) {
    const v = data[k];
    if (v != null && v !== '') {
      const n = typeof v === 'number' ? v : parseFloat(String(v));
      if (Number.isFinite(n)) return n;
    }
  }
  if (data.data != null && typeof data.data === 'object') {
    return parseTransportAmount(data.data);
  }
  return null;
}

/** Pre-submit preview: Georgia insurance matches `vehicleImportCalculatorService.calcInsurance` (1.5% of price, rounded). */
function previewGeorgiaInsuranceUsd(price) {
  const p = parseFloat(String(price));
  if (!Number.isFinite(p) || p <= 0) return 0;
  return Math.round(p * 0.015);
}

/**
 * Pre-submit preview: Armenia insurance matches `calculator.controller.js` `calcInsurance` + server `Math.round`
 * (see `vehicleImportCalculatorService.calculatePrices` + `refCalcInsurance`).
 */
function previewArmeniaInsuranceUsd(price, fob, usd) {
  const carPrice = parseFloat(String(price));
  const f = parseFloat(String(fob)) || 0;
  if (!Number.isFinite(carPrice) || carPrice <= 0) return 0;
  const currencyUSD = Number.isFinite(usd) && usd > 0 ? usd : 400;
  const initialPrice = ((carPrice + f) * 0.95) / 100;
  const raw = initialPrice * currencyUSD <= 28000 ? 28000 / currencyUSD : initialPrice;
  return Math.round(raw);
}

const VehicleImportCalculator = ({
  fromDashboard = false,
  /** Extension/app iframe: `?from=app` + Georgia — POST `from: 'app'` for dealer discount */
  embedFromApp = false,
  onCalculate = null,
  initialQuery = null,
  compact = false,
  hideCountrySwitcher = false,
  /** When set (e.g. from branch), seeds default country before geo-country resolves */
  userCountry = null,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQueryAppliedRef = useRef(false);
  const locWrapperRef = useRef(null);

  const [formData, setFormData] = useState(() => {
    const initialCountry =
      userCountry === 'am' || userCountry === 'ge' ? userCountry : 'ge';
    const defaults = {
      country: initialCountry,
      auction: 2,
      location: '',
      price: '',
      year: new Date().getFullYear(),
      month: '',
      day: '',
      engine: 'gasoline',
      engineVol: 2000,
      carScale: 'sedan',
      carType: false,
      enableInsurance: true,
      customPrice: 0,
      isJuridical: true,
      nash: 0,
      hasR: 0,
      tonnage: 0,
      withoutAah: 0,
      onePointTwoDelta: false,
      destination: 'poti',
    };
    return defaults;
  });

  const [locations, setLocations] = useState([]);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [calculatedAt, setCalculatedAt] = useState(null);
  const [previewTransport, setPreviewTransport] = useState(null);
  /** Geo fetch finished (success or fail) — avoids flash before server default applies */
  const [geoReady, setGeoReady] = useState(() => typeof window === 'undefined');
  const [geoSource, setGeoSource] = useState(null);

  const [locOpen, setLocOpen] = useState(false);
  const [locSearch, setLocSearch] = useState('');
  const [auctionFeeStr, setAuctionFeeStr] = useState('');
  const [transportInputStr, setTransportInputStr] = useState('');
  const [resultModalOpen, setResultModalOpen] = useState(false);
  /** Georgia port destinations from GET /api/calculator/ge-destinations */
  const [geDestinations, setGeDestinations] = useState([]);
  const [geDestinationsLoading, setGeDestinationsLoading] = useState(false);
  /** Georgia vehicle types (per-type US transport add-on) from GET /api/calculator/ge-vehicle-types */
  const [geVehicleTypes, setGeVehicleTypes] = useState([]);
  const [geVehicleTypesLoading, setGeVehicleTypesLoading] = useState(false);
  /** Georgia engine types (optional US transport add-on) from GET /api/calculator/ge-engine-types */
  const [geEngineTypes, setGeEngineTypes] = useState([]);
  const [geEngineTypesLoading, setGeEngineTypesLoading] = useState(false);

  useEffect(() => {
    if (!compact) return;
    const iq =
      initialQuery && typeof initialQuery === 'object' ? Object.keys(initialQuery) : [];
    const debug =
      typeof window !== 'undefined' &&
        (window.__BIDMANAGER_CALC_DEBUG__ === true ||
        window.__AUTOBUY_CALC_DEBUG__ === true ||
        new URLSearchParams(window.location.search).get('calcDebug') === '1');
    if (debug) {
      console.log('[Bidmanager VehicleImportCalculator]', 'calcDebug', {
        fromDashboard,
        initialQuery,
      });
    } else {
      console.info('[Bidmanager VehicleImportCalculator]', 'compact mount', {
        fromDashboard,
        initialQueryKeys: iq,
      });
    }
  }, [compact, fromDashboard, initialQuery]);

  useEffect(() => {
    if (!compact || !error) return;
    console.warn('[Bidmanager VehicleImportCalculator]', 'error', error);
  }, [compact, error]);

  /** One-time: drop legacy persisted country keys (geo API is the source of truth). */
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.removeItem('bidmanager_vehicleImportCalculator_country');
      window.sessionStorage.removeItem('bidmanager_vehicleImportCalculator_country_override');
    } catch {
      /* ignore */
    }
  }, []);

  /** `?country=` is ignored for calculator logic — strip it so old links do not confuse users. */
  useEffect(() => {
    const c = searchParams.get('country');
    if (c == null || c === '') return;
    const next = new URLSearchParams(searchParams);
    next.delete('country');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    let cancelled = false;
    setGeoReady(false);
    (async () => {
      try {
        const data = await DashboardService.getCalculatorGeoCountry();
        if (cancelled) return;
        setGeoSource(data?.source ?? null);
        if (data && (data.country === 'am' || data.country === 'ge')) {
          setFormData((prev) => {
            if (prev.country === data.country) return prev;
            return { ...prev, country: data.country };
          });
        }
      } catch {
        setGeoSource('fallback');
      } finally {
        if (!cancelled) setGeoReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (
      initialQueryAppliedRef.current ||
      !initialQuery ||
      typeof initialQuery !== 'object' ||
      Object.keys(initialQuery).length === 0
    ) {
      return;
    }
    initialQueryAppliedRef.current = true;
    setFormData((prev) => {
      const next = { ...prev };
      if (initialQuery.auction != null && initialQuery.auction !== '') {
        const a = parseInt(String(initialQuery.auction), 10);
        if (!Number.isNaN(a) && a >= 1 && a <= 3) next.auction = a;
      }
      if (initialQuery.price != null && initialQuery.price !== '') next.price = String(initialQuery.price);
      if (initialQuery.year != null && initialQuery.year !== '') {
        const y = parseInt(String(initialQuery.year), 10);
        if (!Number.isNaN(y)) next.year = y;
      }
      if (initialQuery.month != null && initialQuery.month !== '') {
        const m = parseInt(String(initialQuery.month), 10);
        if (!Number.isNaN(m)) next.month = m;
      }
      if (initialQuery.day != null && initialQuery.day !== '') {
        const d = parseInt(String(initialQuery.day), 10);
        if (!Number.isNaN(d)) next.day = d;
      }
      if (initialQuery.engineVol != null && initialQuery.engineVol !== '') {
        const ev = parseInt(String(initialQuery.engineVol), 10);
        if (!Number.isNaN(ev)) next.engineVol = ev;
      }
      if (initialQuery.engineType != null && initialQuery.engineType !== '') {
        next.engine = String(initialQuery.engineType);
      }
      if (initialQuery.carScale != null && initialQuery.carScale !== '') {
        next.carScale = String(initialQuery.carScale);
      }
      if (initialQuery.isJuridical != null && initialQuery.isJuridical !== '') {
        const ij = String(initialQuery.isJuridical).toLowerCase();
        next.isJuridical = ij === 'true' || ij === '1';
      }
      if (initialQuery.carType != null && initialQuery.carType !== '') {
        const ct = String(initialQuery.carType).toLowerCase();
        next.carType = ct === 'true' || ct === '1';
      }
      if (initialQuery.enableInsurance != null && initialQuery.enableInsurance !== '') {
        const ei = String(initialQuery.enableInsurance).toLowerCase();
        next.enableInsurance = ei === 'true' || ei === '1';
      }
      if (initialQuery.destination != null && initialQuery.destination !== '') {
        const d = String(initialQuery.destination).toLowerCase();
        if (/^[a-z0-9-]+$/.test(d)) next.destination = d;
      }
      return next;
    });
  }, [initialQuery]);

  useEffect(() => {
    if (formData.country !== 'ge') {
      return;
    }
    let cancelled = false;
    setGeDestinationsLoading(true);
    (async () => {
      try {
        const list = await DashboardService.getGeDestinations();
        if (cancelled) return;
        setGeDestinations(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error('Georgia destinations:', e);
        if (!cancelled) setGeDestinations([]);
      } finally {
        if (!cancelled) setGeDestinationsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [formData.country]);

  useEffect(() => {
    if (formData.country !== 'ge') return;
    const opts =
      geDestinations.length > 0
        ? geDestinations
        : !geDestinationsLoading
          ? [
              { slug: 'poti' },
              { slug: 'batumi' },
            ]
          : [];
    if (opts.length === 0) return;
    setFormData((prev) => {
      const slugs = new Set(opts.map((d) => d.slug));
      if (slugs.has(prev.destination)) return prev;
      const fallback = opts.find((d) => d.slug === 'poti') || opts[0];
      return { ...prev, destination: fallback ? fallback.slug : 'poti' };
    });
  }, [formData.country, geDestinations, geDestinationsLoading]);

  useEffect(() => {
    if (formData.country !== 'ge') {
      return;
    }
    let cancelled = false;
    setGeVehicleTypesLoading(true);
    (async () => {
      try {
        const list = await DashboardService.getGeVehicleTypes();
        if (cancelled) return;
        setGeVehicleTypes(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error('Georgia vehicle types:', e);
        if (!cancelled) setGeVehicleTypes([]);
      } finally {
        if (!cancelled) setGeVehicleTypesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [formData.country]);

  useEffect(() => {
    if (formData.country !== 'ge') {
      return;
    }
    let cancelled = false;
    setGeEngineTypesLoading(true);
    (async () => {
      try {
        const list = await DashboardService.getGeEngineTypes();
        if (cancelled) return;
        setGeEngineTypes(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error('Georgia engine types:', e);
        if (!cancelled) setGeEngineTypes([]);
      } finally {
        if (!cancelled) setGeEngineTypesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [formData.country]);

  useEffect(() => {
    if (formData.country !== 'ge') return;
    const opts =
      geVehicleTypes.length > 0
        ? geVehicleTypes
        : !geVehicleTypesLoading
          ? [
              { slug: 'sedan', label: t('toolsVehicleImport.scaleSedan'), feeUsd: 20 },
              { slug: 'truck', label: t('toolsVehicleImport.scaleTruck'), feeUsd: 30 },
              { slug: 'motorcycle', label: t('toolsVehicleImport.scaleMotorcycle'), feeUsd: 20 },
              { slug: 'quadbike', label: t('toolsVehicleImport.scaleQuadBike'), feeUsd: 20 },
              { slug: 'snowmobile', label: t('toolsVehicleImport.scaleSnowmobile'), feeUsd: 30 },
            ]
          : [];
    if (opts.length === 0) return;
    setFormData((prev) => {
      const slugs = new Set(opts.map((d) => String(d.slug).toLowerCase()));
      const cur = String(prev.carScale || 'sedan').toLowerCase();
      if (slugs.has(cur)) {
        const match = opts.find((d) => String(d.slug).toLowerCase() === cur);
        if (match && prev.carScale !== match.slug) {
          return { ...prev, carScale: match.slug };
        }
        return prev;
      }
      const fallback =
        opts.find((d) => String(d.slug).toLowerCase() === 'sedan') || opts[0];
      return { ...prev, carScale: fallback ? fallback.slug : 'sedan' };
    });
  }, [formData.country, geVehicleTypes, geVehicleTypesLoading, t]);

  useEffect(() => {
    if (formData.country !== 'ge') return;
    const opts =
      geEngineTypes.length > 0
        ? geEngineTypes
        : !geEngineTypesLoading
          ? [
              { slug: 'gasoline', label: t('toolsVehicleImport.fuelGasoline'), feeUsd: 0 },
              { slug: 'diesel', label: t('toolsVehicleImport.fuelDiesel'), feeUsd: 0 },
              { slug: 'hybrid', label: t('toolsVehicleImport.fuelHybrid'), feeUsd: 0 },
              { slug: 'electric', label: t('toolsVehicleImport.fuelElectric'), feeUsd: 0 },
            ]
          : [];
    if (opts.length === 0) return;
    setFormData((prev) => {
      const slugs = new Set(opts.map((d) => String(d.slug).toLowerCase()));
      const cur = String(prev.engine || 'gasoline').toLowerCase();
      if (slugs.has(cur)) {
        const match = opts.find((d) => String(d.slug).toLowerCase() === cur);
        if (match && prev.engine !== match.slug) {
          return { ...prev, engine: match.slug };
        }
        return prev;
      }
      const fallback = opts.find((d) => String(d.slug).toLowerCase() === 'gasoline') || opts[0];
      return { ...prev, engine: fallback ? fallback.slug : 'gasoline' };
    });
  }, [formData.country, geEngineTypes, geEngineTypesLoading, t]);

  useEffect(() => {
    if (formData.country !== 'am') return;
    const canon = ['sedan', 'truck', 'motorcycle', 'quadBike', 'snowmobile'];
    const raw = String(formData.carScale || 'sedan');
    const byLower = canon.find((c) => c.toLowerCase() === raw.toLowerCase());
    const next = byLower || 'sedan';
    if (next !== formData.carScale) {
      setFormData((p) => ({ ...p, carScale: next }));
    }
  }, [formData.country, formData.carScale]);

  useEffect(() => {
    if (formData.country !== 'am') return;
    const canon = ['gasoline', 'diesel', 'hybrid', 'electric'];
    const raw = String(formData.engine || 'gasoline');
    const byLower = canon.find((c) => c.toLowerCase() === raw.toLowerCase());
    const next = byLower || 'gasoline';
    if (next !== formData.engine) {
      setFormData((p) => ({ ...p, engine: next }));
    }
  }, [formData.country, formData.engine]);

  useEffect(() => {
    const priceNum = parseFloat(String(formData.price)) || 0;
    if (priceNum <= 0) {
      setAuctionFeeStr('');
      return;
    }
    const auctionNum = Number(formData.auction);
    const country =
      formData.country === 'am' || formData.country === 'ge' ? formData.country : 'ge';
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const data = await DashboardService.getFob(priceNum, auctionNum, country);
        if (cancelled) return;
        const fob = data?.fob;
        if (fob != null && Number.isFinite(Number(fob))) {
          setAuctionFeeStr(String(Math.round(Number(fob))));
        } else {
          setAuctionFeeStr('');
        }
      } catch {
        if (!cancelled) setAuctionFeeStr('');
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [formData.price, formData.auction, formData.country]);

  const getLocationForApi = () => {
    const loc = formData.location;
    if (loc == null || loc === '') return null;
    if (typeof loc === 'object' && loc?.id != null) return loc.id;
    return loc;
  };

  useEffect(() => {
    const loc = getLocationForApi();
    if (loc == null || loc === '') {
      setPreviewTransport(null);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const data = await DashboardService.getTransportPrice(
          Number(formData.auction),
          loc,
          formData.country
        );
        const n = parseTransportAmount(data);
        setPreviewTransport(n != null ? n : null);
      } catch {
        setPreviewTransport(null);
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [formData.auction, formData.country, formData.location]);

  useEffect(() => {
    if (previewTransport != null && Number.isFinite(previewTransport)) {
      setTransportInputStr(String(previewTransport));
    } else if (!getLocationForApi()) {
      setTransportInputStr('');
    }
  }, [previewTransport, formData.location]);

  useEffect(() => {
    loadLocations();
    setFormData((prev) => ({ ...prev, location: '' }));
  }, [formData.auction, formData.country]);

  const loadLocations = async () => {
    try {
      setLoading(true);
      const response = await DashboardService.getCalculatorLocations(
        formData.auction,
        formData.country,
        true
      );
      const locs = response?.locations ?? (Array.isArray(response) ? response : []);
      setLocations(locs?.length ? locs : []);
    } catch (err) {
      console.error('Error loading locations:', err);
      setLocations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const applyCountryChange = useCallback(
    (v) => {
      setFormData((prev) => ({
        ...prev,
        country: v,
        ...(v === 'ge'
          ? {
              withoutAah: 0,
              nash: 0,
              onePointTwoDelta: false,
              month: '',
              day: '',
              destination: prev.destination || 'poti',
            }
          : {}),
      }));
      setLocOpen(false);
    },
    []
  );

  const handleEngineChange = (e) => {
    const v = e.target.value;
    setFormData((prev) => ({
      ...prev,
      engine: v,
      ...(v !== 'hybrid' ? { nash: 0 } : {}),
      ...(v !== 'electric' ? { withoutAah: 0 } : {}),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setCalculating(true);
    setCalculatedAt(null);

    try {
      const locationForApi = getLocationForApi();
      const { destination: _omitDest, ...formRest } = formData;
      const params = {
        ...formRest,
        location: locationForApi,
        price: parseFloat(formData.price),
        year: parseInt(formData.year, 10),
        engineVol: parseInt(formData.engineVol, 10),
        month: formData.month ? parseInt(formData.month, 10) : undefined,
        day: formData.day ? parseInt(formData.day, 10) : undefined,
        tonnage: formData.tonnage ? parseInt(formData.tonnage, 10) : 0,
        ...(formData.country === 'ge' && formData.destination
          ? { geDestination: String(formData.destination).toLowerCase() }
          : {}),
      };

      const uid = user?.user_id ?? user?.id ?? searchParams.get('userId');
      if (uid != null && uid !== '') {
        params.userId = String(uid);
      }

      let fromSource = false;
      if (fromDashboard) {
        fromSource = 'dashboard';
      } else if (compact && embedFromApp) {
        fromSource = 'app';
      }

      const result = await DashboardService.calculatePrices(params, fromSource);

      let resultData = result;
      if (result && typeof result === 'object') {
        if (result.success !== undefined && result.data) {
          resultData = result.data;
        } else if (result.data && result.total === undefined) {
          resultData = result.data;
        }
      }

      if (!resultData || resultData.total === undefined) {
        throw new Error(t('toolsVehicleImport.errorInvalidApi'));
      }

      setResults(resultData);
      setCalculatedAt(new Date().toISOString());
      setResultModalOpen(true);
      if (onCalculate) {
        onCalculate(resultData);
      }
    } catch (err) {
      const errorMessage = err.message || t('toolsVehicleImport.errorCalculationFailed');
      setError(errorMessage);
      setResults(null);
      setCalculatedAt(null);
    } finally {
      setCalculating(false);
    }
  };

  const formatCurrency = (value) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value || 0);

  const auctionLabel = (a) =>
    a === 1 ? 'IAAI' : a === 2 ? 'Copart' : a === 3 ? 'Manheim' : String(a);

  const engineTypeLabel = (e) => {
    const key =
      e === 'diesel'
        ? 'fuelDiesel'
        : e === 'hybrid'
          ? 'fuelHybrid'
          : e === 'electric'
            ? 'fuelElectric'
            : 'fuelGasoline';
    return t(`toolsVehicleImport.${key}`);
  };

  const carScaleLabel = (s) => {
    const key =
      s === 'truck'
        ? 'scaleTruck'
        : s === 'motorcycle'
          ? 'scaleMotorcycle'
          : s === 'quadBike'
            ? 'scaleQuadBike'
            : s === 'snowmobile'
              ? 'scaleSnowmobile'
              : 'scaleSedan';
    return t(`toolsVehicleImport.${key}`);
  };

  const locationSummary = () => {
    const loc = formData.location;
    if (loc == null || loc === '') return '—';
    if (typeof loc === 'object' && loc.location != null) return String(loc.location);
    return String(loc);
  };

  const collectFxParts = (r) => {
    if (!r || typeof r !== 'object') return [];
    const parts = [];
    if (r.usdToAmd != null) parts.push(`USD/AMD: ${r.usdToAmd}`);
    if (r.eurToAmd != null) parts.push(`EUR/AMD: ${r.eurToAmd}`);
    if (r.usdToEur != null) parts.push(`USD/EUR: ${r.usdToEur}`);
    if (r.usd_rate != null && r.eur_rate != null && r.amd_rate != null) {
      parts.push(`USD: ${r.usd_rate} · EUR: ${r.eur_rate} · AMD: ${r.amd_rate}`);
    }
    if (parts.length === 0 && r.fx && typeof r.fx === 'object') {
      Object.entries(r.fx).forEach(([k, v]) => {
        if (v != null) parts.push(`${k}: ${v}`);
      });
    }
    return parts;
  };

  const isGe = formData.country === 'ge';
  const auctionIndex = formData.auction - 1;

  const geDestOptions = useMemo(() => {
    if (geDestinations.length > 0) return geDestinations;
    if (geDestinationsLoading) return [];
    return [
      { slug: 'poti', label: t('toolsVehicleImport.destinationPoti'), priceUsd: 0 },
      { slug: 'batumi', label: t('toolsVehicleImport.destinationBatumi'), priceUsd: 100 },
    ];
  }, [geDestinations, geDestinationsLoading, t]);

  const geVehicleTypeOptions = useMemo(() => {
    if (geVehicleTypes.length > 0) return geVehicleTypes;
    if (geVehicleTypesLoading) return [];
    return [
      { slug: 'sedan', label: t('toolsVehicleImport.scaleSedan'), feeUsd: 20 },
      { slug: 'truck', label: t('toolsVehicleImport.scaleTruck'), feeUsd: 30 },
      { slug: 'motorcycle', label: t('toolsVehicleImport.scaleMotorcycle'), feeUsd: 20 },
      { slug: 'quadbike', label: t('toolsVehicleImport.scaleQuadBike'), feeUsd: 20 },
      { slug: 'snowmobile', label: t('toolsVehicleImport.scaleSnowmobile'), feeUsd: 30 },
    ];
  }, [geVehicleTypes, geVehicleTypesLoading, t]);

  const geEngineTypeOptions = useMemo(() => {
    if (geEngineTypes.length > 0) return geEngineTypes;
    if (geEngineTypesLoading) return [];
    return [
      { slug: 'gasoline', label: t('toolsVehicleImport.fuelGasoline'), feeUsd: 0 },
      { slug: 'diesel', label: t('toolsVehicleImport.fuelDiesel'), feeUsd: 0 },
      { slug: 'hybrid', label: t('toolsVehicleImport.fuelHybrid'), feeUsd: 0 },
      { slug: 'electric', label: t('toolsVehicleImport.fuelElectric'), feeUsd: 0 },
    ];
  }, [geEngineTypes, geEngineTypesLoading, t]);

  const carScaleLabelDisplay = (s) => {
    if (formData.country === 'ge' && geVehicleTypeOptions.length > 0) {
      const found = geVehicleTypeOptions.find(
        (d) => String(d.slug).toLowerCase() === String(s).toLowerCase()
      );
      if (found) return found.label;
    }
    return carScaleLabel(s);
  };

  const engineTypeLabelDisplay = (e) => {
    if (formData.country === 'ge' && geEngineTypeOptions.length > 0) {
      const found = geEngineTypeOptions.find(
        (d) => String(d.slug).toLowerCase() === String(e).toLowerCase()
      );
      if (found) return found.label;
    }
    return engineTypeLabel(e);
  };

  const filteredLocs = useMemo(() => {
    const q = locSearch.trim().toLowerCase();
    if (!q) return locations;
    return locations.filter((loc) => {
      const label = (loc.location || loc.label || '').toLowerCase();
      return label.includes(q);
    });
  }, [locations, locSearch]);

  const destPriceUsd = useMemo(() => {
    if (formData.country !== 'ge') return 0;
    const row = geDestOptions.find((d) => d.slug === formData.destination);
    return row != null ? Number(row.priceUsd) || 0 : 0;
  }, [formData.country, formData.destination, geDestOptions]);

  const previewSubtotal = useMemo(() => {
    const p = parseFloat(String(formData.price)) || 0;
    const f = parseFloat(String(auctionFeeStr)) || 0;
    const tr = parseFloat(String(transportInputStr)) || 0;
    let sum = p + f + tr;
    if (formData.country === 'ge') {
      sum += destPriceUsd;
    }
    if (formData.enableInsurance) {
      if (formData.country === 'ge') {
        sum += previewGeorgiaInsuranceUsd(formData.price);
      } else {
        const usd =
          results && results.currencies && results.currencies.USD != null
            ? Number(results.currencies.USD)
            : undefined;
        sum += previewArmeniaInsuranceUsd(formData.price, auctionFeeStr, usd);
      }
    }
    return sum > 0 ? formatCurrency(sum) : '—';
  }, [
    formData.price,
    formData.enableInsurance,
    formData.country,
    auctionFeeStr,
    transportInputStr,
    destPriceUsd,
    results,
  ]);

  const selectAuction = (idx) => {
    setFormData((prev) => ({ ...prev, auction: idx + 1 }));
  };

  const setImporter = (type) => {
    setFormData((prev) => ({
      ...prev,
      isJuridical: type === 'legal',
      onePointTwoDelta: type === 'legal' ? prev.onePointTwoDelta : false,
    }));
  };

  const selectLocationOption = (loc) => {
    const id = loc?.id != null ? loc.id : loc;
    setFormData((prev) => {
      const next = { ...prev, location: loc };
      if (id != null && id !== '') {
        DashboardService.getTransportPrice(Number(prev.auction), id, prev.country)
          .then((data) => {
            const n = parseTransportAmount(data);
            if (n != null) {
              setPreviewTransport(n);
              setTransportInputStr(String(n));
            }
          })
          .catch((err) => {
            console.error('Error loading transport price:', err);
            setPreviewTransport(null);
          });
      }
      return next;
    });
    setLocOpen(false);
    setLocSearch('');
  };

  useEffect(() => {
    const onDoc = (e) => {
      if (locWrapperRef.current && !locWrapperRef.current.contains(e.target)) {
        setLocOpen(false);
      }
    };
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') setResultModalOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const years = useMemo(() => {
    const y0 = new Date().getFullYear() + 1;
    const arr = [];
    for (let y = y0; y >= 1990; y -= 1) arr.push(y);
    return arr;
  }, []);

  const totalUsdWithCommission = (r) => {
    if (!r) return 0;
    if (
      formData.country === 'am' &&
      r.commission != null &&
      typeof r.commission === 'number' &&
      !Number.isNaN(r.commission)
    ) {
      return (Number(r.total) || 0) + Number(r.commission);
    }
    return r.total || 0;
  };

  const renderAuctionRow = () => (
    <div className="auction-row">
      {AUCTION_KEYS.map((key, idx) => (
        <button
          key={key}
          type="button"
          className={`auction-btn ${auctionIndex === idx ? 'vic-active' : ''}`}
          data-auction={key}
          onClick={() => selectAuction(idx)}
        >
          <div className="logo-wrap">
            {idx === 0 && (
              <svg viewBox="0 0 90 46" width="60" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                <text x="2" y="43" fontFamily="system-ui,sans-serif" fontSize="50" fontWeight="900" fill="#cc1111" letterSpacing="-3">
                  /AA
                </text>
                <text x="68" y="46" fontFamily="system-ui,sans-serif" fontSize="13" fill="#cc1111">
                  ®
                </text>
              </svg>
            )}
            {idx === 1 && (
              <svg viewBox="0 0 130 46" width="80" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                <ellipse cx="65" cy="23" rx="62" ry="21" fill="#f8faff" stroke="#aaa" strokeWidth="1.5" />
                <ellipse cx="65" cy="23" rx="55" ry="15" fill="none" stroke="#3a6aaa" strokeWidth="1.2" />
                <text x="65" y="30" textAnchor="middle" fontFamily="system-ui,sans-serif" fontSize="21" fontWeight="800" fill="#1a3a6a" letterSpacing="1">
                  Copart
                </text>
              </svg>
            )}
            {idx === 2 && (
              <svg viewBox="0 0 130 46" width="80" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                <circle cx="23" cy="23" r="21" fill="none" stroke="#1a3a7a" strokeWidth="2.5" />
                <text x="23" y="30" textAnchor="middle" fontFamily="system-ui,sans-serif" fontSize="22" fontWeight="900" fill="#1a4aaa">
                  M
                </text>
                <text x="51" y="20" fontFamily="system-ui,sans-serif" fontSize="16" fontWeight="700" fill="#374151">
                  Manheim
                </text>
                <text x="51" y="35" fontFamily="system-ui,sans-serif" fontSize="11" fill="#9ca3af">
                  by Cox Automotive
                </text>
              </svg>
            )}
          </div>
          <span className="auction-name">{auctionLabel(idx + 1)}</span>
        </button>
      ))}
    </div>
  );

  const renderLocationField = () => (
    <div className="field" style={{ position: 'relative' }} ref={locWrapperRef}>
      <label>
        {t('toolsVehicleImport.locationOptional')} <span className="req">*</span>
      </label>
      <div className="custom-select" id="loc-wrapper">
        <div
          className={`custom-select-trigger ${locOpen ? 'vic-open' : ''}`}
          id="loc-trigger"
          role="button"
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation();
            setLocOpen((o) => !o);
            if (!locOpen) setLocSearch('');
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setLocOpen((o) => !o);
            }
          }}
        >
          <span style={{ color: formData.location ? 'var(--text)' : 'var(--text-dim)' }}>
            {locationSummary()}
          </span>
          <svg width="10" height="6" viewBox="0 0 10 6" style={{ flexShrink: 0, transform: locOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>
            <path fill="currentColor" fillOpacity="0.45" d="M5 6L0 0h10z" />
          </svg>
        </div>
        {locOpen && (
          <div className="custom-select-dropdown" id="loc-dropdown">
            <div style={{ padding: '7px 9px', borderBottom: `1px solid var(--border)` }}>
              <input
                type="text"
                className="vic-loc-search-input"
                id="loc-search"
                placeholder={t('toolsVehicleImport.searchLocationPlaceholder')}
                value={locSearch}
                onChange={(e) => setLocSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            <div style={{ maxHeight: 160, overflowY: 'auto' }}>
              {loading && (
                <div className="loc-option" style={{ color: 'var(--text-dim)' }}>
                  …
                </div>
              )}
              {!loading &&
                filteredLocs.map((loc) => {
                  const id = loc.id;
                  const selected =
                    typeof formData.location === 'object' && formData.location?.id === id;
                  return (
                    <div
                      key={id}
                      className={`loc-option ${selected ? 'vic-selected' : ''}`}
                      role="option"
                      aria-selected={selected}
                      onClick={() => selectLocationOption(loc)}
                    >
                      {loc.location || loc.label || id}
                    </div>
                  );
                })}
              {!loading && filteredLocs.length === 0 && (
                <div className="loc-option" style={{ color: 'var(--text-dim)', fontSize: 14 }}>
                  —
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderArmeniaForm = () => (
    <>
      {renderAuctionRow()}
      <div className="field-label">
        {t('toolsVehicleImport.userTypeRequired')} <span className="req">*</span>
      </div>
      <div className="imp-row">
        <button
          type="button"
          className={`imp-btn ${!formData.isJuridical ? 'vic-active' : ''}`}
          id="btn-individual"
          onClick={() => setImporter('individual')}
        >
          {t('toolsVehicleImport.userIndividual')}
        </button>
        <button
          type="button"
          className={`imp-btn ${formData.isJuridical ? 'vic-active' : ''}`}
          id="btn-legal"
          onClick={() => setImporter('legal')}
        >
          {t('toolsVehicleImport.userCompany')}
        </button>
      </div>

      <div className="row2">
        <div className="field">
          <label>
            {t('toolsVehicleImport.vehiclePriceUsd')} <span className="req">*</span>
          </label>
          <input
            type="number"
            name="price"
            value={formData.price}
            onChange={handleChange}
            min={0}
            step={0.01}
            required
          />
        </div>
        <div className="field">
          <label>{t('toolsVehicleImport.resultFob')}</label>
          <input
            type="number"
            value={auctionFeeStr}
            onChange={(e) => setAuctionFeeStr(e.target.value)}
            min={0}
            step={1}
          />
        </div>
      </div>

      {renderLocationField()}

      <div className="field">
        <label>{t('toolsVehicleImport.resultTransport')}</label>
        <input
          type="number"
          value={transportInputStr}
          onChange={(e) => setTransportInputStr(e.target.value)}
          placeholder={t('toolsVehicleImport.previewTransportEstimate')}
        />
      </div>

      <div className="vic-fob-line">
        <span className="vic-fob-line-label">{t('toolsVehicleImport.resultTotalCost')}</span>
        <span className="vic-fob-line-val">{previewSubtotal}</span>
      </div>

      <div className="section-label">{t('toolsVehicleImport.armeniaDateSection')}</div>
      <div className="row3" style={{ marginBottom: 14 }}>
        <select
          value={formData.year}
          onChange={(e) => setFormData((p) => ({ ...p, year: parseInt(e.target.value, 10) }))}
        >
          {years.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
        <select
          value={formData.month === '' ? '' : String(formData.month).padStart(2, '0')}
          onChange={(e) =>
            setFormData((p) => ({ ...p, month: e.target.value ? parseInt(e.target.value, 10) : '' }))
          }
        >
          <option value="">{t('toolsVehicleImport.vehicleMonth')}</option>
          {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
            <option key={m} value={String(m).padStart(2, '0')}>
              {String(m).padStart(2, '0')}
            </option>
          ))}
        </select>
        <select
          value={formData.day === '' ? '' : String(formData.day).padStart(2, '0')}
          onChange={(e) =>
            setFormData((p) => ({ ...p, day: e.target.value ? parseInt(e.target.value, 10) : '' }))
          }
        >
          <option value="">{t('toolsVehicleImport.vehicleDay')}</option>
          {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
            <option key={d} value={String(d).padStart(2, '0')}>
              {String(d).padStart(2, '0')}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label>
          {t('toolsVehicleImport.vehicleTypeRequired')} <span className="req">*</span>
        </label>
        <select name="carScale" value={formData.carScale} onChange={handleChange} required>
          <option value="sedan">{t('toolsVehicleImport.scaleSedan')}</option>
          <option value="truck">{t('toolsVehicleImport.scaleTruck')}</option>
          <option value="motorcycle">{t('toolsVehicleImport.scaleMotorcycle')}</option>
          <option value="quadBike">{t('toolsVehicleImport.scaleQuadBike')}</option>
          <option value="snowmobile">{t('toolsVehicleImport.scaleSnowmobile')}</option>
        </select>
      </div>

      <div className="row2">
        <div className="field">
          <label>
            {t('toolsVehicleImport.engineTypeRequired')} <span className="req">*</span>
          </label>
          <select name="engine" value={formData.engine} onChange={handleEngineChange} required>
            <option value="gasoline">{t('toolsVehicleImport.fuelGasoline')}</option>
            <option value="diesel">{t('toolsVehicleImport.fuelDiesel')}</option>
            <option value="hybrid">{t('toolsVehicleImport.fuelHybrid')}</option>
            <option value="electric">{t('toolsVehicleImport.fuelElectric')}</option>
          </select>
        </div>
        <div className="field">
          <label>
            {t('toolsVehicleImport.engineVolumeCc')} <span className="req">*</span>
          </label>
          <input
            type="number"
            name="engineVol"
            value={formData.engineVol}
            onChange={handleChange}
            min={0}
            required
          />
        </div>
      </div>

      <div className="chk-group">
        <label className="chk-row">
          <input
            type="checkbox"
            name="enableInsurance"
            checked={formData.enableInsurance}
            onChange={handleChange}
          />
          <div className="toggle-track">
            <div className="toggle-thumb" />
          </div>
          <span>{t('toolsVehicleImport.includeInsurance')}</span>
        </label>
        <label className="chk-row">
          <input type="checkbox" name="carType" checked={formData.carType} onChange={handleChange} />
          <div className="toggle-track">
            <div className="toggle-thumb" />
          </div>
          <span>{t('toolsVehicleImport.highClearanceVehicle')}</span>
        </label>
        {formData.country === 'am' && formData.isJuridical && (
          <label className="chk-row">
            <input
              type="checkbox"
              checked={formData.onePointTwoDelta}
              onChange={(e) =>
                setFormData((p) => ({ ...p, onePointTwoDelta: e.target.checked }))
              }
            />
            <div className="toggle-track">
              <div className="toggle-thumb" />
            </div>
            <span>{t('toolsVehicleImport.onePointTwoCoefficient')}</span>
          </label>
        )}
      </div>

      {formData.country === 'am' &&
        (formData.engine === 'electric' ||
          formData.engine === 'hybrid' ||
          formData.isJuridical) && (
          <div
            style={{
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius)',
              padding: compact ? 12 : 16,
              marginBottom: 12,
              background: 'var(--surface2)',
            }}
          >
            {formData.engine === 'electric' && (
              <label className="chk-row">
                <input
                  type="checkbox"
                  checked={formData.withoutAah === 1}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, withoutAah: e.target.checked ? 1 : 0 }))
                  }
                />
                <div className="toggle-track">
                  <div className="toggle-thumb" />
                </div>
                <span>{t('toolsVehicleImport.withoutVatAah')}</span>
              </label>
            )}
            {formData.engine === 'hybrid' && (
              <label className="chk-row">
                <input
                  type="checkbox"
                  checked={formData.nash === 1}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, nash: e.target.checked ? 1 : 0 }))
                  }
                />
                <div className="toggle-track">
                  <div className="toggle-thumb" />
                </div>
                <span>{t('toolsVehicleImport.hybridNash')}</span>
              </label>
            )}
          </div>
        )}

      <button type="submit" className="calc-btn" disabled={calculating}>
        {calculating ? <span className="vic-spinner" /> : null}
        {calculating ? t('toolsVehicleImport.calculating') : t('toolsVehicleImport.calculate')}
      </button>
    </>
  );

  const renderGeorgiaForm = () => (
    <>
      {renderAuctionRow()}
      <div className="row2">
        <div className="field">
          <label>
            {t('toolsVehicleImport.vehiclePriceUsd')} <span className="req">*</span>
          </label>
          <input
            type="number"
            name="price"
            value={formData.price}
            onChange={handleChange}
            min={0}
            step={0.01}
            placeholder="Enter vehicle price"
            required
          />
        </div>
        <div className="field">
          <label>{t('toolsVehicleImport.resultFob')}</label>
          <input
            type="number"
            value={auctionFeeStr}
            onChange={(e) => setAuctionFeeStr(e.target.value)}
          />
        </div>
      </div>
      {renderLocationField()}
      <div className="field">
        <label>{t('toolsVehicleImport.resultTransport')}</label>
        <input
          type="number"
          value={transportInputStr}
          onChange={(e) => setTransportInputStr(e.target.value)}
        />
      </div>
      <div className="field">
        <label>
          {t('toolsVehicleImport.destination')} <span className="req">*</span>
        </label>
        <select
          name="destination"
          value={formData.destination}
          onChange={handleChange}
          required
          disabled={geDestinationsLoading && geDestOptions.length === 0}
        >
          {geDestinationsLoading && geDestOptions.length === 0 && (
            <option value="poti">{t('common.loading')}</option>
          )}
          {geDestOptions.map((d) => (
            <option key={d.slug} value={d.slug}>
              {d.label}
              {Number(d.priceUsd) > 0 ? ` (+$${Number(d.priceUsd).toFixed(0)})` : ''}
            </option>
          ))}
        </select>
      </div>
      <div className="row2">
        <div className="field">
          <label>
            {t('toolsVehicleImport.vehicleTypeRequired')} <span className="req">*</span>
          </label>
          <select
            name="carScale"
            value={formData.carScale}
            onChange={handleChange}
            required
            disabled={geVehicleTypesLoading && geVehicleTypeOptions.length === 0}
          >
            {geVehicleTypesLoading && geVehicleTypeOptions.length === 0 && (
              <option value="sedan">{t('common.loading')}</option>
            )}
            {geVehicleTypeOptions.map((d) => (
              <option key={d.slug} value={d.slug}>
                {d.label}
                {Number(d.feeUsd) > 0 ? ` (+$${Number(d.feeUsd).toFixed(0)})` : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>
            {t('toolsVehicleImport.engineTypeRequired')} <span className="req">*</span>
          </label>
          <select
            name="engine"
            value={formData.engine}
            onChange={handleEngineChange}
            required
            disabled={geEngineTypesLoading && geEngineTypeOptions.length === 0}
          >
            {geEngineTypesLoading && geEngineTypeOptions.length === 0 && (
              <option value="gasoline">{t('common.loading')}</option>
            )}
            {geEngineTypeOptions.map((d) => (
              <option key={d.slug} value={d.slug}>
                {d.label}
                {Number(d.feeUsd) > 0 ? ` (+$${Number(d.feeUsd).toFixed(0)})` : ''}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="field">
        <label>
          {t('toolsVehicleImport.engineVolumeCc')} <span className="req">*</span>
        </label>
        <input
          type="number"
          name="engineVol"
          value={formData.engineVol}
          onChange={handleChange}
          min={0}
          required
        />
      </div>
      <div className="vic-fob-line">
        <span className="vic-fob-line-label">{t('toolsVehicleImport.resultTotalCost')}</span>
        <span className="vic-fob-line-val">{previewSubtotal}</span>
      </div>
      <div className="chk-group">
        <label className="chk-row">
          <input
            type="checkbox"
            name="enableInsurance"
            checked={formData.enableInsurance}
            onChange={handleChange}
          />
          <div className="toggle-track">
            <div className="toggle-thumb" />
          </div>
          <span>{t('toolsVehicleImport.includeInsurance')}</span>
        </label>
      </div>
      <button type="submit" className="calc-btn" disabled={calculating}>
        {calculating ? <span className="vic-spinner" /> : null}
        {calculating ? t('toolsVehicleImport.calculating') : t('toolsVehicleImport.calculate')}
      </button>
    </>
  );

  const r = results;

  const renderAmResultModal = () => {
    if (!r) return null;
    const fx = collectFxParts(r);
    const ts = calculatedAt
      ? new Date(calculatedAt).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'medium' })
      : '—';
    return (
      <div
        className={`modal-overlay ${resultModalOpen ? 'vic-active' : ''}`}
        role="presentation"
        onClick={(e) => {
          if (e.target === e.currentTarget) setResultModalOpen(false);
        }}
      >
        <div className="modal" id="modal" onClick={(e) => e.stopPropagation()}>
          <button type="button" className="modal-close" onClick={() => setResultModalOpen(false)} aria-label="Close">
            ✕
          </button>
          <div className="cur-bar">
            <div className="cur-rates">
              <div className="cur-item">
                <div className="cur-label">FX</div>
                <div className="cur-value" style={{ fontSize: 13, maxWidth: 200, textAlign: 'center' }}>
                  {fx.length ? fx.join(' · ') : '—'}
                </div>
              </div>
            </div>
            <div className="cur-time" id="m-timestamp">
              {ts}
            </div>
          </div>
          <div className="m-meta1">
            <span className="hl">{formData.isJuridical ? t('toolsVehicleImport.userCompany') : t('toolsVehicleImport.userIndividual')}</span>
            <span className="sep">/</span>
            <span className="hl">{carScaleLabel(formData.carScale)}</span>
          </div>
          <div className="m-meta2">
            <div className="m-badges" id="m-detail">
              <div className="m-badge">
                <span className="b-key">Auction</span>
                <span className="b-val">{auctionLabel(formData.auction)}</span>
              </div>
              <div className="m-badge">
                <span className="b-key">Location</span>
                <span className="b-val">{locationSummary()}</span>
              </div>
            </div>
          </div>
          <div className="r-grid">
            <div className="r-col">
              <div className="r-row">
                <span className="r-lbl">{t('toolsVehicleImport.resultVehiclePrice')}</span>
                <span className="r-sep">/</span>
                <span className="r-val">{formatCurrency(r.price)}</span>
              </div>
              <div className="r-row">
                <span className="r-lbl">{t('toolsVehicleImport.resultFob')}</span>
                <span className="r-sep">/</span>
                <span className="r-val">{formatCurrency(r.fob)}</span>
              </div>
              <div className="r-row">
                <span className="r-lbl">{t('toolsVehicleImport.resultTransport')}</span>
                <span className="r-sep">/</span>
                <span className="r-val">{formatCurrency(r.transport)}</span>
              </div>
              <div className="r-row">
                <span className="r-lbl">{t('toolsVehicleImport.resultInsurance')}</span>
                <span className="r-sep">/</span>
                <span className="r-val">{formatCurrency(r.insurance)}</span>
              </div>
            </div>
            <div className="r-col">
              <div className="r-row">
                <span className="r-lbl">{t('toolsVehicleImport.resultImportTax')}</span>
                <span className="r-sep">/</span>
                <span className="r-val">{formatCurrency(r.tax)}</span>
              </div>
              <div className="r-row">
                <span className="r-lbl">{t('toolsVehicleImport.resultVatAah')}</span>
                <span className="r-sep">/</span>
                <span className="r-val">{formatCurrency(r.aah)}</span>
              </div>
              <div className="r-row">
                <span className="r-lbl">{t('toolsVehicleImport.resultBnph')}</span>
                <span className="r-sep">/</span>
                <span className="r-val">{formatCurrency(r.bnph)}</span>
              </div>
              <div className="r-row">
                <span className="r-lbl">{t('toolsVehicleImport.resultDiscount')}</span>
                <span className="r-sep">/</span>
                <span className="r-val">{r.discount != null ? formatCurrency(r.discount) : '—'}</span>
              </div>
            </div>
          </div>
          <div className="m-total-wrap">
            <div className="m-total-lbl">{t('toolsVehicleImport.resultTotalCost')}</div>
            <div className="m-total-val">{formatCurrency(totalUsdWithCommission(r))}</div>
            <div className="m-total-amd" id="m-total-amd">
              {calculatedAt && (
                <span style={{ fontSize: 13 }}>
                  {t('toolsVehicleImport.calculationTimestampUtc')}: {calculatedAt}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderGeResultModal = () => {
    if (!r) return null;
    const destLabelForResult =
      r.geDestinationLabel ||
      geDestOptions.find((d) => d.slug === r.geDestination)?.label ||
      (r.geDestination === 'batumi'
        ? t('toolsVehicleImport.destinationBatumi')
        : r.geDestination === 'poti'
          ? t('toolsVehicleImport.destinationPoti')
          : r.geDestination || '—');
    const fx = collectFxParts(r);
    const ts = calculatedAt
      ? new Date(calculatedAt).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'medium' })
      : '—';
    return (
      <div
        className={`modal-overlay ${resultModalOpen ? 'vic-active' : ''}`}
        role="presentation"
        onClick={(e) => {
          if (e.target === e.currentTarget) setResultModalOpen(false);
        }}
      >
        <div className="modal" onClick={(e) => e.stopPropagation()}>
          <button type="button" className="modal-close" onClick={() => setResultModalOpen(false)} aria-label="Close">
            ✕
          </button>
          <div className="cur-bar">
            <div className="cur-rates">
              <div className="cur-item">
                <div className="cur-label">FX</div>
                <div className="cur-value" style={{ fontSize: 13 }}>
                  {fx.length ? fx.join(' · ') : '—'}
                </div>
              </div>
            </div>
            <div className="cur-time">{ts}</div>
          </div>
          <div className="m-meta" id="m-meta">
            <span className="hl">{auctionLabel(formData.auction)}</span> / {locationSummary()} /{' '}
            <span className="hl">{engineTypeLabelDisplay(formData.engine)}</span>
            {' / '}
            <span className="hl">{carScaleLabelDisplay(formData.carScale)}</span>
          </div>
          <table className="rt" id="result-table">
            <tbody>
              <tr>
                <td>{t('toolsVehicleImport.resultVehiclePrice')}</td>
                <td className="sep">/</td>
                <td className="val">{formatCurrency(r.price)}</td>
              </tr>
              <tr>
                <td>{t('toolsVehicleImport.resultFob')}</td>
                <td className="sep">/</td>
                <td className="val">{formatCurrency(r.fob)}</td>
              </tr>
              {r.discount != null && Number(r.discount) > 0 && r.transportOriginal != null && (
                <tr>
                  <td>{t('toolsVehicleImport.resultTransportListPrice')}</td>
                  <td className="sep">/</td>
                  <td className="val">{formatCurrency(r.transportOriginal)}</td>
                </tr>
              )}
              {r.discount != null && Number(r.discount) > 0 && (
                <tr>
                  <td>
                    {r.discountPercent != null && Number(r.discountPercent) > 0
                      ? t('toolsVehicleImport.resultDiscount').replace('{percent}', String(r.discountPercent))
                      : t('toolsVehicleImport.resultDiscountAmount')}
                  </td>
                  <td className="sep">/</td>
                  <td className="val">−{formatCurrency(r.discount)}</td>
                </tr>
              )}
              <tr>
                <td>{t('toolsVehicleImport.resultTransport')}</td>
                <td className="sep">/</td>
                <td className="val">{formatCurrency(r.transport)}</td>
              </tr>
              <tr>
                <td>{t('toolsVehicleImport.resultInsurance')}</td>
                <td className="sep">/</td>
                <td className="val">{formatCurrency(r.insurance)}</td>
              </tr>
              <tr>
                <td>{t('toolsVehicleImport.resultImportTax')}</td>
                <td className="sep">/</td>
                <td className="val">{formatCurrency(r.tax)}</td>
              </tr>
              <tr>
                <td>{t('toolsVehicleImport.resultVatAah')}</td>
                <td className="sep">/</td>
                <td className="val">{formatCurrency(r.aah)}</td>
              </tr>
              <tr>
                <td>{t('toolsVehicleImport.resultBnph')}</td>
                <td className="sep">/</td>
                <td className="val">{formatCurrency(r.bnph)}</td>
              </tr>
              <tr>
                <td>
                  {t('toolsVehicleImport.resultDestinationSurcharge')}
                  {r.geDestination != null ? ` (${destLabelForResult})` : ''}
                </td>
                <td className="sep">/</td>
                <td className="val">{formatCurrency(r.destinationSurcharge ?? 0)}</td>
              </tr>
            </tbody>
          </table>
          <div className="formula-box">
            <p>{t('toolsVehicleImport.exchangeRatesAtCalculation')}</p>
            <p>{collectFxParts(r).join(' · ') || t('toolsVehicleImport.exchangeRatesPending')}</p>
          </div>
          <div className="m-total-bar">
            <span className="tl">{t('toolsVehicleImport.resultTotalCost')}</span>
            <span className="tv">{formatCurrency(r.total)}</span>
          </div>
        </div>
      </div>
    );
  };

  /** Embed: top-right — country name only (no control). */
  const countryEmbedLabel = () => (
    <div className="vic-embed-country-label" id="calculatorCountryDisplay">
      {!geoReady ? (
        <span className="vic-embed-country-label-loading">{t('toolsVehicleImport.geoChecking')}</span>
      ) : (
        <>
          <span className="vic-embed-country-label-text">
            {formData.country === 'am'
              ? t('toolsVehicleImport.countryArmenia')
              : t('toolsVehicleImport.countryGeorgia')}
          </span>
          {geoSource === 'fallback' && (
            <span className="vic-embed-country-label-hint" title="Local / private IP">
              {' '}
              · localhost
            </span>
          )}
        </>
      )}
    </div>
  );

  const countryReadonly = () => (
    <div className="vic-country-readonly" id="calculatorCountrySwitch">
      {!geoReady ? (
        <span className="vic-country-readonly-loading">{t('toolsVehicleImport.geoChecking')}</span>
      ) : (
        <>
          <span className="vic-country-readonly-label">
            {formData.country === 'am'
              ? t('toolsVehicleImport.countryArmenia')
              : t('toolsVehicleImport.countryGeorgia')}
          </span>
          {geoSource === 'fallback' && (
            <span className="vic-country-readonly-hint" title="Local / private IP">
              · localhost
            </span>
          )}
        </>
      )}
    </div>
  );

  const countryPillMain = () => (
    <div className="vic-country-pill" id="calculatorCountrySwitch">
      <button
        type="button"
        className={formData.country === 'am' ? 'vic-active' : ''}
        onClick={() => applyCountryChange('am')}
      >
        {t('toolsVehicleImport.countryArmenia')}
      </button>
      <button
        type="button"
        className={formData.country === 'ge' ? 'vic-active' : ''}
        onClick={() => applyCountryChange('ge')}
      >
        {t('toolsVehicleImport.countryGeorgia')}
      </button>
    </div>
  );

  /** Embed: second row — minimal AM/GE control (short labels). */
  const countryPillTiny = () => (
    <div
      className="vic-country-pill vic-country-pill--tiny"
      id="calculatorCountrySwitch"
      role="group"
      aria-label="AM / GE"
    >
      <button
        type="button"
        className={formData.country === 'am' ? 'vic-active' : ''}
        onClick={() => applyCountryChange('am')}
        title={t('toolsVehicleImport.countryArmenia')}
      >
        AM
      </button>
      <button
        type="button"
        className={formData.country === 'ge' ? 'vic-active' : ''}
        onClick={() => applyCountryChange('ge')}
        title={t('toolsVehicleImport.countryGeorgia')}
      >
        GE
      </button>
    </div>
  );

  return (
    <div
      className={`vic-root ${isGe ? 'vic--ge' : 'vic--am'} ${compact ? 'vic--compact' : ''}`}
      data-import-calc="1"
      data-country={formData.country}
    >
      <div className="vic-drawer">
        <div className={`vic-drawer-header${compact ? ' vic-drawer-header--embed' : ''}`}>
          <div className="vic-drawer-header-top">
            {compact ? (
              <div className="vic-header-actions vic-header-actions--embed">
                {countryEmbedLabel()}
                {!hideCountrySwitcher && countryPillTiny()}
              </div>
            ) : (
              <div className="vic-header-actions">
                {hideCountrySwitcher ? countryReadonly() : countryPillMain()}
              </div>
            )}
          </div>
        </div>

        <div className="vic-drawer-body">
          {error && <div className="vic-error">{error}</div>}
          <form id="form-scroll" className="vic-form-scroll" onSubmit={handleSubmit}>
            {isGe ? renderGeorgiaForm() : renderArmeniaForm()}
          </form>

          {results && (isGe ? renderGeResultModal() : renderAmResultModal())}
        </div>
      </div>
    </div>
  );
};

export default VehicleImportCalculator;
